<script setup lang="ts">
import { ref } from "vue";
import Icon from "@/design/primitives/Icon.vue";
import TextAreaField from "@/design/primitives/TextAreaField.vue";
import { useAgentsI18n } from "../i18n/use-agents-i18n";

/**
 * 👍/👎 sob a resposta do agente no painel, e o caminho até o turno que a produziu.
 *
 * O 👎 abre um painel INLINE, nunca um modal: quem acabou de ler uma resposta
 * errada está lendo a conversa, e um modal tapa exatamente o texto que a pessoa
 * precisa ter na frente para dizer o que houve. O campo é texto livre — uma lista
 * fechada de motivos é um palpite sobre o que vai dar errado, feito antes de
 * qualquer coisa ter dado.
 *
 * ── O GÊMEO NA INBOX ──────────────────────────────────────────────────────
 *
 * `features/inbox/components/message/TurnRating.vue` faz o mesmo para a bolha do
 * cliente. Não é importado daqui porque uma feature nunca importa outra
 * (`no-cross-feature`), e mover o componente exigiria mexer no lado da inbox. No
 * dia em que um terceiro leitor aparecer, os dois sobem juntos para `shared/`.
 */
const props = defineProps<{ turnId: string }>();

const emit = defineEmits<{
	rate: [payload: { rating: 1 | -1; note?: string }];
	/**
	 * O painel do motivo abriu ou fechou.
	 *
	 * A barra que hospeda isto some quando o rato sai da fala, e escrever o motivo
	 * de um 👎 far-se-ia com a caixa a desaparecer debaixo do cursor. Quem prega a
	 * barra é ela própria; o que falta é ela SABER.
	 */
	open: [boolean];
}>();

const { t } = useAgentsI18n();

/** What has already been said about THIS reply, so the thumb stays lit. */
const given = ref<1 | -1 | null>(null);
const open = ref(false);
const note = ref("");

function thumbUp(): void {
	given.value = 1;
	setOpen(false);
	emit("rate", { rating: 1 });
}

function setOpen(value: boolean): void {
	open.value = value;
	emit("open", value);
}

function thumbDown(): void {
	// O 👎 já vale sozinho: o painel pede o porquê, não o exige.
	given.value = -1;
	setOpen(true);
	emit("rate", { rating: -1 });
}

function sendNote(): void {
	emit("rate", { rating: -1, ...(note.value.trim() ? { note: note.value.trim() } : {}) });
	note.value = "";
	setOpen(false);
}
</script>

<template>
	<!-- `contents`: os dois filhos entram direto na fila flex da barra — os polegares
	     ficam na fila, entre copiar e regerar, e o painel do motivo quebra para uma linha
	     inteira por baixo, em vez de se espremer ao lado. -->
	<div class="contents">
		<!-- O mesmo traço de 12px da barra (padrão V2): aceso quando é a opinião
		     dada, apagado e à espera quando não é. -->
		<div class="flex items-center gap-0.5 text-xs">
			<button
				type="button"
				class="flex size-9 sm:size-7 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
				:class="given === 1 ? 'bg-success-subtle text-success' : 'hover:bg-bg-muted hover:text-success'"
				:aria-pressed="given === 1"
				:title="t('rating.up')"
				:aria-label="t('rating.up')"
				@click="thumbUp"
			>
				<Icon name="thumbsUp" class="size-5 sm:size-4" />
			</button>
			<button
				type="button"
				class="flex size-9 sm:size-7 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
				:class="given === -1 ? 'bg-danger-subtle text-danger' : 'hover:bg-bg-muted hover:text-danger'"
				:aria-expanded="open"
				:aria-pressed="given === -1"
				:title="t('rating.down')"
				:aria-label="t('rating.down')"
				@click="thumbDown"
			>
				<Icon name="thumbsDown" class="size-5 sm:size-4" />
			</button>
		</div>

		<div v-if="open" class="mt-1 flex w-full items-start gap-1.5">
			<div class="min-w-0 flex-1">
				<TextAreaField
					v-model="note"
					:rows="2"
					class="text-xs"
					:placeholder="t('rating.whatWasWrong')"
					:aria-label="t('rating.whatWasWrongLabel')"
				/>
			</div>
			<button
				type="button"
				class="shrink-0 rounded-md border border-border px-2 py-1 text-xs text-fg-muted hover:bg-bg-muted"
				@click="sendNote"
			>
				{{ t("rating.send") }}
			</button>
		</div>
	</div>
</template>
